
import { AppBar, Toolbar, Collapse, Stack } from "@mui/material";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { createTheme, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import Swipeable from "../swipeable/Swipeable";
import BreadCrumbV2Desktop from "../bread-crumb/BreadCrumbV2Desktop";
import React, { useMemo } from "react";
import BackButton from "../back-button/BackButton";
import { useLocation } from "react-router";
import { getNavigationRoutes } from "../../common/utilities/GetNavigationRoutes";
import { useAvailableViews } from "../../queries/available-views/UseAvailableViews";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { isMobile, isTablet } from 'react-device-detect';
import { routes, UIViewCategory } from "../../routes/routes";
import OrganizationPicker from "../organization-picker/OrganizationPicker";
import { IOrganization } from "../../services/organization-data-service/Models";
import { ContextActionMenu } from "./ContextActionMenu";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import GroupPicker from "../group-picker/GroupPicker";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import BreadCrumb from "../bread-crumb/BreadCrumb";
import { ContextualAppBarProps } from "./ContextualAppBar";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(0),
    // Override media queries injected by theme.mixins.toolbar
    //   '@media all': {
    //     minHeight: 128,
    //   },
}));

// export type ContextualAppBarProps = {
//     viewOptionState?: ViewOptionStateEnum;
//     onExecuteAction?: (action: ActionMenuItem) => void;
//     getRefreshAction?: () => ActionMenuItem;
//     actions?: ActionMenuItem[];
//     configurationActions?: ActionMenuItem[];
//     hideBreadcrumb?: boolean;
//     showOrganizationPicker?: boolean;
//     onOrganizationSelected?: (item: IOrganization) => void;
//     organizationID?: number;

//     showGroupPicker?: boolean;
//     onGroupSelected?: (item: INavigationItem) => void;
//     groupID?: number;
//     hideOptionsAction?: boolean;
//     dense?: boolean;
//     label?: string;
//     viewMaximized?: boolean;

//     onOverrideViewOptionsClick?: () => void;
//     hideMaximizeViewAction?: boolean;

//     sx?: SxProps<Theme>;
//     onGoingBack?: () => boolean;
// };

export default function ContextualAppBarV1({
    onExecuteAction,
    actions,
    configurationActions,
    viewOptionState,
    hideBreadcrumb,
    showOrganizationPicker,
    onOrganizationSelected,
    organizationID,
    showGroupPicker,
    onGroupSelected,
    groupID,
    hideOptionsAction,
    dense,
    label,
    viewMaximized,
    onOverrideViewOptionsClick,
    hideMaximizeViewAction,
    sx,
    onGoingBack
}: ContextualAppBarProps) {

    const isPhone = isMobile && !isTablet;

    const location = useLocation();
    const [appSettings] = useAppSettings();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();

    // const theme = useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV1) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);

    const { data: availableViews } = useQueryNotifications(useAvailableViews)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const navigationRoutes = getNavigationRoutes(availableViews ?? [], isPhone);
    const currentRoute = navigationRoutes.find(o => o.fullPath === location.pathname);
    const currentParentRoute = currentRoute?.parentViewID !== undefined ? navigationRoutes.find(o => o.viewID === currentRoute?.parentViewID) : undefined;
    const route = routes.find(o => o.fullPath === location.pathname);
    const parentRoute = route?.parentViewID !== undefined ? routes.find(o => o.viewID === route?.parentViewID) : undefined;

    const manualEntryView = availableViews?.find(o => o === UIViewEnum.Device_ManualEntry);

    const title = label ? label :
        route?.viewID === UIViewEnum.Device_GaugeSheet && manualEntryView !== undefined
            ? routes.find(o => o.viewID === UIViewEnum.Device_ManualEntry)?.name
            : currentParentRoute?.name ?? currentRoute?.name ?? parentRoute?.name ?? route?.name;

    // const title = route?.viewID === UIViewEnum.Device_GaugeSheet &&  manualEntryView !== undefined
    //     ? routes.find(o => o.viewID === UIViewEnum.Device_ManualEntry)?.name
    //     : (currentRoute?.parentViewID !== undefined
    //         ? navigationRoutes.find(o => o.viewID === currentRoute.parentViewID)?.name
    //         : currentRoute?.name
    //         ) ?? routes.find(o => o.viewID === route?.parentViewID)?.name ?? route?.name;

    const adminView = route?.category === UIViewCategory.AdminGroup || route?.category === UIViewCategory.AdminView;
    const showBackButton = isPhone && (routeContext.contextObject?.objectType !== eLynxObjectTypeEnum.Group || adminView);

    function onRefresh() {
        const refreshAction: ActionMenuItem = {
            label: 'refresh',
            action: BaseActionEnum.Refresh
        };

        onExecuteAction?.(refreshAction);
    }

    function onOrganizationChanged(item: IOrganization) {
        onOrganizationSelected?.(item);
    }

    function onGroupChanged(item: INavigationItem) {
        onGroupSelected?.(item);
    }

    const hideAddToFavoritesAction = !(currentRoute || parentRoute) || dense;

    return (dense ? <Stack
        direction='row'
        sx={{
            justifyContent: 'space-between',
            marginRight: 2,
            ...sx
        }}>

        <Typography
            variant="subtitle1"
            noWrap
            component="div"
            sx={{
                textTransform: "uppercase",
                fontWeight: 600,
                flex: "1 1 auto",
                flexGrow: 1,
                alignSelf: 'center',
                overflow: 'hidden',
                marginLeft: 1
            }}>
            {title}
        </Typography>

        <ContextActionMenu
            viewOptionState={viewOptionState}
            onExecuteAction={onExecuteAction}
            actions={actions}
            configurationActions={configurationActions}
            hideAddToFavoritesAction={hideAddToFavoritesAction}
            hideOptionsAction={hideOptionsAction}
            showMaximizeViewAction={!hideMaximizeViewAction}
            maximizeView={viewMaximized}
            onOverrideViewOptionsClick={onOverrideViewOptionsClick}
        />

    </Stack> : !isPhone && appSettings.maximizeContent ?
        <Swipeable enablePullToRefresh={true} onRefresh={onRefresh} >
            <AppBar position="static" color='default'
                sx={{
                    paddingTop: theme => theme.spacing(1),
                    paddingBottom: theme => theme.spacing(1),
                    paddingRight: theme => theme.spacing(4),
                    // minHeight: isPhone ? 136 : 68
                    flexDirection: 'row'
                }}>
                {
                    !hideBreadcrumb &&
                    <>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                marginLeft: theme => theme.spacing(3),
                                display: 'flex',
                                flex: '1 1 auto'
                            }}>
                            {navigationV2
                                ? <BreadCrumbV2Desktop />
                                : <BreadCrumb />
                            }
                        </Typography>

                        {appSettings.maximizeContent &&
                            <ContextActionMenu
                                viewOptionState={viewOptionState}
                                onExecuteAction={onExecuteAction}
                                actions={actions}
                                configurationActions={configurationActions}
                                hideAddToFavoritesAction={hideAddToFavoritesAction}
                                hideOptionsAction={hideOptionsAction} />
                        }
                    </>
                }






                {
                    showOrganizationPicker &&
                    <OrganizationPicker
                        organizationID={organizationID ?? userState.user?.baseGroup?.organizationID as number}
                        onSelected={onOrganizationChanged}
                    />
                }

                {
                    showGroupPicker && groupID &&
                    <GroupPicker
                        groupID={groupID}
                        onSelected={onGroupChanged}
                    />
                }

            </AppBar>
        </Swipeable>

        :


        <Collapse in={!appSettings.maximizeContent} sx={{ minHeight: 'auto!important' }}>
            <Swipeable enablePullToRefresh={true} onRefresh={onRefresh} >
                <AppBar position="static" color={isPhone ? 'primary' : 'default'} sx={{ paddingBottom: theme => theme.spacing(1), minHeight: isPhone ? 136 : 68 }}>
                    <StyledToolbar>

                        {showBackButton &&
                            <BackButton
                                color={"text.primary"}
                                onGoingBack={onGoingBack}
                            />
                        }

                        {!isPhone &&
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{
                                    flexGrow: 1,
                                    alignSelf: 'center'
                                }}>
                                {title}
                            </Typography>
                        }

                        {
                            <ContextActionMenu
                                viewOptionState={viewOptionState}
                                onExecuteAction={onExecuteAction}
                                actions={actions}
                                configurationActions={configurationActions}
                                hideAddToFavoritesAction={hideAddToFavoritesAction}
                                hideOptionsAction={hideOptionsAction} />
                        }

                    </StyledToolbar>

                    {isPhone && <Typography
                        variant="h5"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            marginLeft: theme => theme.spacing(7),
                            marginRight: theme => theme.spacing(3)
                        }}
                    >
                        {title}
                    </Typography>
                    }

                    {
                        !hideBreadcrumb &&
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                marginLeft: theme => isPhone ? theme.spacing(6) : theme.spacing(3),
                                marginRight: theme => isPhone ? 0 : theme.spacing(3),
                            }}>
                            {navigationV2 && !isPhone
                                ? <BreadCrumbV2Desktop />
                                : <BreadCrumb />
                            }
                        </Typography>
                    }


                    {
                        showOrganizationPicker &&
                        <OrganizationPicker
                            organizationID={organizationID ?? userState.user?.baseGroup?.organizationID as number}
                            onSelected={(item) => onOrganizationSelected && onOrganizationSelected(item)}
                        />
                    }

                    {
                        showGroupPicker && groupID &&
                        <GroupPicker
                            groupID={groupID}
                            onSelected={onGroupChanged}
                        />}


                </AppBar>
            </Swipeable>
        </Collapse>);
}
